import type { LucideIcon } from 'lucide-react';
import {
  ClipboardCheck,
  Thermometer,
  Wrench,
  Eye,
  Scan,
  Layers,
  ListChecks,
} from 'lucide-react';
import type { SpoolTrackerWeldStepType } from './spoolNcrApi';
import { spoolTw } from './spoolUiPalette';

/**
 * Short copy for NCR tables: what pills are inserted into the single workflow strip after this step’s NCR.
 * Base flow is seven steps (Fit-Up → … → Accept); repair rows extend that sequence in place.
 */
export function getRepairStripInsertionSummary(sourceStep: SpoolTrackerWeldStepType): string {
  switch (sourceStep) {
    case 'welding':
      return 'After Weld: Repair Weld → VT → NDT → Coating → Accept.';
    case 'vt':
      return 'After first VT: Repair Weld → VT (retest) → NDT → Coating → Accept.';
    case 'ndt':
      return 'After first NDT: Repair Weld → VT (retest) → NDT (retest) → Coating → Accept.';
    case 'fitup':
    case 'preheat':
    case 'coating':
    case 'accept':
    case 'repair_weld':
    default:
      return 'After Weld: Repair Weld → VT → NDT → Coating → Accept.';
  }
}

/** Panel / modal routing — maps to `renderPanel` branches in `SpoolDetailModal`. */
export type WorkflowPanelRoute =
  | 'fitup'
  | 'preheat'
  | 'welding'
  | 'repair_weld'
  | 'vt'
  | 'ndt'
  | 'coating'
  | 'accept';

export type WorkflowStripItem = {
  /** Stable unique key for navigation (supports duplicate VT/NDT labels). */
  stripKey: string;
  panel: WorkflowPanelRoute;
  /** Same VT panel; distinguish retest saves in payload when backend is wired. */
  vtPhase?: 'standard' | 'retest';
  ndtPhase?: 'standard' | 'retest';
  label: string;
  short: string;
  Icon: LucideIcon;
  role: string;
  roleColor: string;
  /** Full repair cycle pill (Fit-Up n … Accept n), appended after production Accept. */
  repairCycle?: {
    repairNo: number;
    cycleStep: 'fitup' | 'preheat' | 'welding' | 'vt' | 'ndt' | 'coating' | 'accept';
  };
};

function meta(
  panel: WorkflowPanelRoute,
  short: string,
  label: string,
  Icon: LucideIcon,
  role: string,
  roleColor: string
): Omit<WorkflowStripItem, 'stripKey' | 'vtPhase' | 'ndtPhase'> {
  return { panel, short, label, Icon, role, roleColor };
}

/** Normal progression — one row per production step (7 pills). */
export function buildBaseWorkflowStrip(): WorkflowStripItem[] {
  return [
    { stripKey: 'fitup', ...meta('fitup', 'Fit-Up', 'Fit-Up', ClipboardCheck, 'QC Inspector', spoolTw.roleQcPill) },
    { stripKey: 'preheat', ...meta('preheat', 'Preheat', 'Preheat', Thermometer, 'Welder', 'bg-blue-100 text-blue-700') },
    { stripKey: 'welding', ...meta('welding', 'Weld', 'Weld', Wrench, 'Welder', 'bg-blue-100 text-blue-700') },
    { stripKey: 'vt', ...meta('vt', 'VT', 'Visual (VT)', Eye, 'QC Inspector', spoolTw.roleQcPill), vtPhase: 'standard' },
    { stripKey: 'ndt', ...meta('ndt', 'NDT', 'NDT', Scan, 'NDT Operator Lv II', 'bg-orange-100 text-orange-700'), ndtPhase: 'standard' },
    { stripKey: 'coating', ...meta('coating', 'Coating', 'Coating', Layers, 'Coating Inspector', 'bg-purple-100 text-purple-700') },
    { stripKey: 'accept', ...meta('accept', 'Accept', 'Accept', ListChecks, 'QC Inspector', 'bg-green-100 text-green-700') },
  ];
}

/**
 * When an NCR triggers a repair cycle, inserts **Repair Weld** and any required retests into one continuous strip
 * (no separate abbreviated strip). Patterns align with shop flow:
 * - **Welding** NCR → Repair Weld immediately after Weld, then VT → NDT → Coating → Accept.
 * - **VT** NCR → after first VT: Repair Weld → VT (retest) → NDT → …
 * - **NDT** NCR → after first NDT: Repair Weld → VT retest → NDT retest → …
 *
 * Backend can later mirror this order via `weld.repairs[]` / step payloads.
 */
export function buildIntegratedRepairWorkflowStrip(args: {
  repairFlowActive: boolean;
  repairSourceStep: SpoolTrackerWeldStepType | null;
}): WorkflowStripItem[] {
  const base = buildBaseWorkflowStrip();
  if (!args.repairFlowActive || !args.repairSourceStep) return base;

  const rw = {
    stripKey: 'repair_weld',
    ...meta('repair_weld', 'Repair Weld', 'Repair Weld', Wrench, 'Welder', 'bg-blue-100 text-blue-700'),
  };

  switch (args.repairSourceStep) {
    case 'welding': {
      // 1 Fit-Up … 3 Weld | 4 Repair Weld | 5 VT … 8 Accept
      const i = base.findIndex((s) => s.panel === 'welding');
      if (i < 0) return base;
      const next = base.slice();
      next.splice(i + 1, 0, rw);
      return next;
    }
    case 'vt': {
      // … | VT | Repair Weld | VT (retest) | NDT | …
      const i = base.findIndex((s) => s.panel === 'vt');
      if (i < 0) return base;
      const next = base.slice();
      const vtRetest: WorkflowStripItem = {
        stripKey: 'vt_retest',
        panel: 'vt',
        vtPhase: 'retest',
        short: 'VT',
        label: 'Visual (VT) — retest',
        Icon: Eye,
        role: 'QC Inspector',
        roleColor: spoolTw.roleQcPill,
      };
      next.splice(i + 1, 0, rw, vtRetest);
      return next;
    }
    case 'ndt': {
      // … | NDT | Repair Weld | VT retest | NDT retest | …
      const i = base.findIndex((s) => s.panel === 'ndt');
      if (i < 0) return base;
      const next = base.slice();
      const vtRetest: WorkflowStripItem = {
        stripKey: 'vt_retest',
        panel: 'vt',
        vtPhase: 'retest',
        short: 'VT',
        label: 'Visual (VT) — retest',
        Icon: Eye,
        role: 'QC Inspector',
        roleColor: spoolTw.roleQcPill,
      };
      const ndtRetest: WorkflowStripItem = {
        stripKey: 'ndt_retest',
        panel: 'ndt',
        ndtPhase: 'retest',
        short: 'NDT',
        label: 'NDT — retest',
        Icon: Scan,
        role: 'NDT Operator Lv II',
        roleColor: 'bg-orange-100 text-orange-700',
      };
      next.splice(i + 1, 0, rw, vtRetest, ndtRetest);
      return next;
    }
    case 'fitup':
    case 'preheat':
    case 'coating':
    case 'accept':
    case 'repair_weld':
    default: {
      // Conservative default: repair weld immediately after **Weld** (same as welding-step NCR).
      const i = base.findIndex((s) => s.panel === 'welding');
      if (i < 0) return base;
      const next = base.slice();
      next.splice(i + 1, 0, rw);
      return next;
    }
  }
}
